// import { useState, useEffect } from 'react';
// import { Box, Toolbar } from '@mui/material';

// import { Outlet, useLocation } from 'react-router-dom';
// import { useAppDispatch, useAppSelector } from '../../store/hooks';
// import { openDrawer } from '../../store/reducers/menu';
// import Header from '../../components/Header';
// import MainDrawer from '../../components/drawer';
// import CommonAlert from '@/components/third-party/alert';
// import useMenuPathValidation from '@/store/reducers/useMenuPathValidation';
// import { exclusionPatterns } from '@/utils/constants';

// const Mainlayout = () => {
//   //const theme = useTheme();
//   // const matchDownLG = useMediaQuery(theme.breakpoints.down("lg"));
//   const location = useLocation();
//   const dispatch = useAppDispatch();
//   const { isAlertOpen, alertMsg, severity } = useAppSelector(
//     (state) => state.appReducer
//   );
//   const { drawerOpen } = useAppSelector((state) => state.menu);

//   const [open, setOpen] = useState(true);
//   const [isFullscreen, setIsFullscreen] = useState(false);
//   const actualPath =
//     location.pathname != '/'
//       ? location.pathname.replace(/\/$/, '')
//       : location.pathname;
//   const validateMenuPath = useMenuPathValidation(actualPath);

//   useEffect(() => {
//     // Validate the current path
//     const isExcluded = exclusionPatterns.some((pattern: any) =>
//       location.pathname.startsWith(pattern)
//     );
//     // Add paths to exclude from validation
//     if (!isExcluded) {
//       validateMenuPath(actualPath, 'Query');
//     }
//   }, [location.pathname]);

//   const handleDrawerToggle = () => {
//     setOpen(!open);
//     dispatch(openDrawer({ drawerOpen: !open }));
//   };

//   // Temporarily disabled to debug drawer issue
//   // useEffect(() => {
//   //   // Only auto-close drawer on mobile, but don't auto-open/close on desktop
//   //   if (!isFullscreen && matchDownLG) {
//   //     setOpen(false);
//   //     dispatch(openDrawer({ drawerOpen: false }));
//   //   }
//   //   // eslint-disable-next-line react-hooks/exhaustive-deps
//   // }, [matchDownLG, isFullscreen]);
//   // Toggle Fullscreen Mode
//   const handleFullscreenToggle = () => {
//     setIsFullscreen((prev) => !prev);
//     setOpen(isFullscreen); // Close sidebar when fullscreen is enabled
//   };

//   useEffect(() => {
//     if (open !== drawerOpen) setOpen(drawerOpen);
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [drawerOpen]);

//   return (
//     <Box
//       sx={{
//         display: 'flex',
//         width: '100%',
//         backgroundColor: '#0a1628',
//       }}
//     >
//       <Header open={open} handleDrawerToggle={handleDrawerToggle} />
//       <MainDrawer open={open} handleDrawerToggle={handleDrawerToggle} />
//       <Box
//         component="main"
//         sx={{
//           width: '100%',
//           flexGrow: 1,
//           p: 0,
//           backgroundColor: '#F8FAFC',
//           minHeight: '100vh',
//         }}
//       >
//         <Toolbar />
//         {/* <Breadcrumbs navigation={navigation} title /> */}
//         <Outlet context={{ isFullscreen, handleFullscreenToggle }} />
//         {isAlertOpen ? (
//           <CommonAlert children={alertMsg} severity={severity} />
//         ) : null}
//       </Box>
//     </Box>
//   );
// };

// export default Mainlayout;

import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import { Outlet, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { openDrawer } from '../../store/reducers/menu';
import Header from '../../components/Header';
import MainDrawer from '../../components/drawer';
import CommonAlert from '@/components/third-party/alert';
import useMenuPathValidation from '@/store/reducers/useMenuPathValidation';
import { exclusionPatterns } from '@/utils/constants';
import ReportJobsTray from '@/views/HR/reports/jobs/ReportJobsTray';
import ReviewBanner from '@/views/Admin/AccessReview/ReviewBanner';
import { ShortcutProvider } from '@/shortcuts/ShortcutProvider';

const Mainlayout = () => {
  const theme = useTheme();
  // below lg the drawer is a temporary overlay (components/drawer uses the same breakpoint)
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'), { noSsr: true });
  // const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  const location = useLocation();
  const dispatch = useAppDispatch();
  const { isAlertOpen, alertMsg, severity } = useAppSelector(
    (state) => state.appReducer
  );
  const { drawerOpen } = useAppSelector((state) => state.menu);
  // a company/division switch reloads the open page, as if reopened from the
  // menu, so every screen fetches the new division's data
  const { loggedUserData } = useAppSelector((state) => state.loginReducer);
  const divisionKey = `${loggedUserData?.comp_code ?? ''}|${loggedUserData?.divn_code ?? ''}`;
  // cached query data (many lookups keep it 5-10 min, mostly without the
  // division in the key) belongs to the old division: drop it and refetch
  const queryClient = useQueryClient();
  const lastDivision = useRef(divisionKey);
  useEffect(() => {
    if (lastDivision.current === divisionKey) return;
    lastDivision.current = divisionKey;
    queryClient.resetQueries();
  }, [divisionKey, queryClient]);

  // the drawer's open state lives only in the store; a local copy kept in sync by effects
  // could re-open it after the size check closed it
  const open = drawerOpen;
  const [isFullscreen, setIsFullscreen] = useState(false);

  const actualPath =
    location.pathname !== '/'
      ? location.pathname.replace(/\/$/, '')
      : location.pathname;

  const validateMenuPath = useMenuPathValidation(actualPath);

  // closed on small screens, open on large ones; before paint, so a phone never flashes the menu
  useLayoutEffect(() => {
    dispatch(openDrawer({ drawerOpen: !isMobile }));
  }, [isMobile, dispatch]);

  // Close drawer on route change on mobile
  useEffect(() => {
    if (isMobile) {
      dispatch(openDrawer({ drawerOpen: false }));
    }

    const isExcluded = exclusionPatterns.some((pattern: any) =>
      location.pathname.startsWith(pattern)
    );
    if (!isExcluded) {
      validateMenuPath(actualPath, 'Query');
    }
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    dispatch(openDrawer({ drawerOpen: !open }));
  };

  const handleFullscreenToggle = () => {
    setIsFullscreen((prev) => {
      const next = !prev;
      // Close sidebar when entering fullscreen, restore on exit
      dispatch(openDrawer({ drawerOpen: !next }));
      return next;
    });
  };

  // Drawer width constants — must match your MainDrawer component
  const DRAWER_WIDTH = 260;

  return (
    // Oracle Forms style keyboard shortcuts on every screen
    <ShortcutProvider>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          minHeight: '100vh',
          // dvh: the visible height on phones (vh includes the area under the browser bar)
          '@supports (height: 100dvh)': { minHeight: '100dvh' },
          backgroundColor: '#0a1628',
        }}
      >
        {/* Header */}
        <Header open={open} handleDrawerToggle={handleDrawerToggle} />

        {/* Sidebar */}
        <MainDrawer open={open} handleDrawerToggle={handleDrawerToggle} />

        {/* Mobile overlay — closes drawer when tapping outside */}
        {isMobile && open && (
          <Box
            onClick={handleDrawerToggle}
            sx={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.45)',
              zIndex: (theme) => theme.zIndex.drawer - 1,
            }}
          />
        )}

        {/* Main content */}
        {/* Main content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: {
              xs: '100%',
              lg:
                open && !isFullscreen
                  ? `calc(100% - ${DRAWER_WIDTH}px)`
                  : '100%',
            },
            minWidth: 0, // ← prevents flex child from overflowing
            maxWidth: '100%', // ← hard cap at viewport width
            minHeight: '100vh',
            '@supports (height: 100dvh)': { minHeight: '100dvh' },
            backgroundColor: '#F8FAFC',
            p: 0,
            // clip, not hidden: hidden turns main into a scroll container and
            // breaks position: sticky inside pages
            overflowX: 'clip',
            transition: theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            ...(open &&
              !isMobile &&
              !isFullscreen && {
                transition: theme.transitions.create(['width', 'margin'], {
                  easing: theme.transitions.easing.easeOut,
                  duration: theme.transitions.duration.enteringScreen,
                }),
              }),
          }}
        >
          <Toolbar />
          {/* reminder for open access reviews (division heads, HR) */}
          <ReviewBanner />
          <Outlet key={divisionKey} context={{ isFullscreen, handleFullscreenToggle }} />
          {isAlertOpen && (
            <CommonAlert severity={severity}>{alertMsg}</CommonAlert>
          )}
        </Box>

        {/* report jobs keep running (and stay visible) across screens */}
        <ReportJobsTray />
      </Box>
    </ShortcutProvider>
  );
};

export default Mainlayout;
